import { useState } from "react";
import { FaEdit, FaTrash, FaArrowCircleRight } from "react-icons/fa";
import { Tooltip } from "react-tooltip";

// Composants
import DistributionDropdown from "../../CorporateAction/DistributionModeDropdown";

// Utilitaires et Hooks
import { 
  canDistributeEvent, 
  getEventDisplayInfo 
} from "../../service/utils/eventUtils";
import { formatAmountInThousands, formatDate, getDateStyle } from "../../service/utils/formatters";
import { formatAmount } from "../../service/utils/orderUtils";
import { useSmartDropdownEvent } from "../../service/Hooks/useSmartDropdownEvent";

/**
 * LIGNE DE TABLEAU ÉVÉNEMENT - Gère l'affichage et les actions d'une ligne d'événement OST
 * @param {Object} props
 * @param {Object} props.event - Données de l'événement
 * @param {Function} props.onEdit - Callback pour éditer l'événement
 * @param {Function} props.onDelete - Callback pour supprimer l'événement
 * @param {Function} props.onDetails - Callback pour voir les détails
 * @param {Function} props.onDistributeClick - Callback pour distribuer l'événement
 */
const EventTablesRow = ({
  event,
  onEdit,
  onDelete,
  onDetails,
  onDistributeClick
}) => {
  const { displayStatus, statusClass } = getEventDisplayInfo(event);
  const dateStyle = getDateStyle(event.dateDePlanification);
  
  // Utilisation du hook useSmartDropdownEvent pour gérer le dropdown des actions
  const {
    isOpen,
    position,
    triggerRef,
    dropdownRef,
    toggleDropdown,
    closeDropdown
  } = useSmartDropdownEvent();

  /**
   * Gère le clic sur l'action Modifier
   */
  const handleEditClick = () => {
    closeDropdown();
    onEdit(event);
  };

  /**
   * Gère le clic sur l'action Supprimer
   */
  const handleDeleteClick = () => {
    closeDropdown();
    onDelete(event);
  };

  /**
   * Gère le clic sur l'action Détails
   */
  const handleDetailsClick = () => {
    closeDropdown();
    onDetails(event);
  };

  return (
    <tr key={event.id}>
      {/* Colonne Type */}
      <td>{event.type}</td>
      
      {/* Colonne Montant */}
      <td>
        <span className="counter-amount-badge">
          {event.amount ? formatAmountInThousands(event.amount) : "N/A"}
        </span>
      </td>
      
      {/* Colonne Nombre de titres */}
      <td>
        <span className="counter-amount-badge">
          {event.nombreTitre ? (event.nombreTitre) : "N/A"}
        </span>
      </td>
      
      {/* Colonne Capital */}
      <td>
        <span className="counter-amount-badge">
          {event.capital > 0 ? (event.capital) : "N/A"} %
        </span>
      </td>
      
      {/* Colonne Titre */}
      <td>{event.securityName}</td>
      
      {/* Colonne ISIN */}
      <td style={{paddingLeft:"40px"}}>{event.securityIsin}</td>
      
      {/* Colonne Marché */}
      <td>{event.targetMarket}</td>
      
      {/* Colonne Échéance */}
      <td style={dateStyle}>
        {formatDate(event.dateDePlanification)}
      </td>
      
      {/* Colonne Statut */}
      <td>
        <span className={`badge rounded-pill ${statusClass}`}>
          {displayStatus}
        </span>
      </td>
      
      {/* Colonne Distribuer */}
      <td className="text-center">
        {canDistributeEvent(event) ? (
          <DistributionDropdown
            onDistributeClick={onDistributeClick}
            event={event}
          />
        ) : (
          <span className="text-muted">-</span>
        )}
        <Tooltip id={`distribute-tooltip-${event.id}`} place="top" effect="solid" />
      </td>
      
      {/* Colonne Actions avec dropdown personnalisé */}
      <td>
        <div className="actions-dropdown-container position-relative">
          {/* Bouton de déclenchement du dropdown */}
          <button
            ref={triggerRef}
            type="button"
            className="btn btn-light light sharp"
            onClick={toggleDropdown}
            aria-expanded={isOpen}
            aria-label="Menu des actions"
            style={{
              padding: "2px 5px",
              minWidth: "20px",
              minHeight: "20px"
            }}
          >
            <svg
              width="16px"
              height="16px"
              viewBox="0 0 24 24"
              version="1.1"
            >
              <g
                stroke="none"
                strokeWidth="1"
                fill="none"
                fillRule="evenodd"
              >
                <rect x="0" y="0" width="24" height="24" />
                <circle fill="#000000" cx="5" cy="12" r="2" />
                <circle fill="#000000" cx="12" cy="12" r="2" />
                <circle fill="#000000" cx="19" cy="12" r="2" />
              </g>
            </svg>
          </button>

          {/* Overlay pour fermer le dropdown en cliquant à l'extérieur */}
          {isOpen && <div className="dropdown-overlay" onClick={closeDropdown} />}

          {/* Dropdown des actions avec positionnement intelligent */}
          {isOpen && (
            <div
              ref={dropdownRef}
              className="dropdown-menu-custom show shadow-lg"
              style={{
                position: "fixed",
                top: `${position.top}px`,
                left: `${position.left}px`,
                zIndex: 1050,
                minWidth: "200px",
                backgroundColor: "white",
                borderRadius: "8px",
                border: "1px solid rgba(0,0,0,0.15)",
                boxShadow: "0 6px 12px rgba(0,0,0,0.175)"
              }}
              role="menu"
              aria-orientation="vertical"
            >
              {/* Action: Modifier */}
              <button
                type="button"
                className="dropdown-item-custom d-flex align-items-center w-100 px-3 py-2 text-start"
                onClick={handleEditClick}
                role="menuitem"
                style={{
                  border: "none",
                  backgroundColor: "transparent",
                  cursor: "pointer",
                  transition: "background-color 0.2s"
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f8f9fa"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
              >
                <FaEdit size={14} className="me-2" color="#007bff" />
                <span className="text-dark">Modifier</span>
              </button>

              {/* Séparateur */}
              <div className="dropdown-divider my-1" style={{ borderTop: "1px solid #e9ecef" }} />

              {/* Action: Supprimer */}
              <button
                type="button"
                className="dropdown-item-custom d-flex align-items-center w-100 px-3 py-2 text-start"
                onClick={handleDeleteClick}
                role="menuitem"
                style={{
                  border: "none",
                  backgroundColor: "transparent",
                  cursor: "pointer",
                  transition: "background-color 0.2s"
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f8f9fa"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
              >
                <FaTrash size={12} className="me-2" color="#dc3545" />
                <span className="text-danger">Supprimer</span>
              </button>

              {/* Séparateur */}
              <div className="dropdown-divider my-1" style={{ borderTop: "1px solid #e9ecef" }} />

              {/* Action: Détails */}
              <button
                type="button"
                className="dropdown-item-custom d-flex align-items-center w-100 px-3 py-2 text-start"
                onClick={handleDetailsClick}
                role="menuitem"
                style={{
                  border: "none",
                  backgroundColor: "transparent",
                  cursor: "pointer",
                  transition: "background-color 0.2s"
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f8f9fa"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
              >
                <FaArrowCircleRight size={12} className="me-2" color="#28a745" />
                <span className="text-dark">Détails OST</span>
              </button>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

export default EventTablesRow;